import Constants from '@/Constants';
import { getViewTypeData } from '@/models/ViewType';
import { ApplicationState } from '@/store';
import {
    clearSearchSuggestions,
    deleteSearchHistoryRequest,
    getSearchHistoryRequest,
    getSearchSuggestionsRequest,
    searchChange,
    searchClear,
    searchSubmit,
} from '@/store/search/action';
import { SearchHistory } from '@/store/search/types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import './style.scss';

enum SearchInputStatus {
    SHOWN = 1,
    HIDDEN = 0,
    PAGE_LOADING = -1,
}

const Search: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const term = useSelector((state: ApplicationState) => state.search.data.term);
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { searchHistoryList, suggestionsList, lastSearch } = useSelector((state: ApplicationState) => ({
        searchHistoryList: state.search.data.history,
        suggestionsList: state.search.data.suggestions,
        lastSearch: state.search.data.lastTerm,
    }));

    const [showSearchInput, setShowSearchInput] = useState<SearchInputStatus>(SearchInputStatus.PAGE_LOADING);

    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownRecentBtnRef = useRef<HTMLButtonElement>(null);
    const dropdownRecentContainerRef = useRef<HTMLDivElement>(null);
    const dropdownSuggestBtnRef = useRef<HTMLButtonElement>(null);
    const dropdownSuggestContainerRef = useRef<HTMLDivElement>(null);

    const MAX_LIMIT_SEARCH_HISTORY = 6;

    const onClearTextPressHandler = () => {
        if (term && term.trim().length > 0) {
            dispatch(searchClear());
        } else {
            setShowSearchInput(SearchInputStatus.HIDDEN);
        }
    };

    const onSearchTextPressHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (showSearchInput === SearchInputStatus.SHOWN) {
            if (term && term.trim().length > 0) {
                dispatch(searchSubmit());
                history.push(`/${getViewTypeData('listSearchResultStudentView').resource}`);
            }
        } else {
            setShowSearchInput(SearchInputStatus.SHOWN);
        }
    };

    const onSearchTextChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(searchChange(event.target.value));
    };

    const onSearchTextClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.stopPropagation();
        manageFocusOnSearchInputAndUpdateDropdownVisibility();
    };

    const manageFocusOnSearchInputAndUpdateDropdownVisibility = (forceClose: boolean = false) => {
        if (showSearchInput === SearchInputStatus.SHOWN) {
            if (
                dropdownRecentContainerRef != null &&
                dropdownRecentContainerRef.current != null &&
                dropdownSuggestContainerRef != null &&
                dropdownSuggestContainerRef.current != null
            ) {
                let isRecentOpen: boolean = dropdownRecentContainerRef.current.classList.contains('show');
                let isSuggestOpen: boolean = dropdownSuggestContainerRef.current.classList.contains('show');
                const isSearchEmpty: boolean = term.trim().length === 0;
                const isClickOnRecentOrSuggestTerm: boolean = lastSearch === term;

                if (forceClose || isClickOnRecentOrSuggestTerm) {
                    if (isRecentOpen) {
                        dropdownRecentBtnRef && dropdownRecentBtnRef.current && dropdownRecentBtnRef.current.click();
                    } else if (isSuggestOpen) {
                        dropdownSuggestBtnRef && dropdownSuggestBtnRef.current && dropdownSuggestBtnRef.current.click();
                    }
                    return;
                }

                if (!isRecentOpen && isSearchEmpty) {
                    searchInputRef && searchInputRef.current && searchInputRef.current.focus();

                    if (searchHistoryList.length > 0) {
                        dropdownRecentBtnRef && dropdownRecentBtnRef.current && dropdownRecentBtnRef.current.click();
                        isRecentOpen = true;
                        isSuggestOpen = false;
                    }
                }

                if (isRecentOpen && !isSearchEmpty) {
                    dropdownRecentBtnRef && dropdownRecentBtnRef.current && dropdownRecentBtnRef.current.click();
                    isRecentOpen = false;
                    isSuggestOpen = false;
                }

                if (!isSuggestOpen && !isSearchEmpty && suggestionsList.length > 0) {
                    dropdownSuggestBtnRef && dropdownSuggestBtnRef.current && dropdownSuggestBtnRef.current.click();
                    isRecentOpen = false;
                    isSuggestOpen = true;
                }

                if (isSuggestOpen && (isSearchEmpty || suggestionsList.length === 0)) {
                    dropdownSuggestBtnRef && dropdownSuggestBtnRef.current && dropdownSuggestBtnRef.current.click();
                    isRecentOpen = false;
                    isSuggestOpen = false;
                }
            }
        }
    };

    const onClearPressHandler = () => {
        dispatch(deleteSearchHistoryRequest());
    };

    const onTermPressHandler = (item: string) => {
        dispatch(searchChange(item));
        dispatch(searchSubmit());
        history.push(`/${getViewTypeData('listSearchResultStudentView').resource}`);
    };

    const escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === 27) {
            manageFocusOnSearchInputAndUpdateDropdownVisibility(true);
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', escFunction, false);

        return () => {
            document.removeEventListener('keydown', escFunction, false);
        };
    }, [escFunction]);

    useEffect(() => {
        if (term.trim().length > 0) {
            setShowSearchInput(SearchInputStatus.SHOWN);
        }
        dispatch(getSearchHistoryRequest());
    }, [dispatch]);

    useEffect(() => {
        const termIsEmpty: boolean = term.trim().length === 0;
        if (termIsEmpty) {
            dispatch(clearSearchSuggestions());
        } else {
            const timeOutId = setTimeout(() => dispatch(getSearchSuggestionsRequest(term)), 300);
            return () => clearTimeout(timeOutId);
        }
    }, [dispatch, term]);

    useEffect(manageFocusOnSearchInputAndUpdateDropdownVisibility, [showSearchInput, term, suggestionsList]);

    return (
        <>
            <div className="container search-container">
                <form onSubmit={onSearchTextPressHandler}>
                    <button
                        className={`btn-search btn-search${
                            showSearchInput === SearchInputStatus.SHOWN
                                ? '--input-shown '
                                : showSearchInput === SearchInputStatus.HIDDEN
                                ? '--input-hidden'
                                : ''
                        }`}
                        type="submit"
                    >
                        <i className="material-icons-outlined">{Constants.icons.SEARCH}</i>
                    </button>
                    <input
                        className={` search-input search-input${
                            showSearchInput === SearchInputStatus.SHOWN ? '--shown' : showSearchInput === SearchInputStatus.HIDDEN ? '--hidden' : ''
                        }`}
                        onChange={onSearchTextChangeHandler}
                        onClick={onSearchTextClickHandler}
                        ref={searchInputRef}
                        type="text"
                        value={term}
                    />
                    {showSearchInput === SearchInputStatus.SHOWN && (
                        <button className="close-button" onClick={() => onClearTextPressHandler()} type="reset">
                            <i className="material-icons-outlined close-icon">{Constants.icons.CLOSE}</i>
                        </button>
                    )}
                </form>
            </div>
            <div className="search-dropdown dropdown">
                <button className="btn btn-danger dropdown-toggle" data-toggle="dropdown" ref={dropdownRecentBtnRef} type="button"></button>
                <div className="search-dropdown-container dropdown-menu dropdown-menu-right" ref={dropdownRecentContainerRef}>
                    <div className="search-list">
                        <div className="title-container">
                            <h1 className="title">{strings.RECENT_SEARCHES}</h1>
                            <button className="btn btn-link" onClick={onClearPressHandler}>
                                {strings.CLEAR}
                            </button>
                        </div>
                        {searchHistoryList.slice(0, MAX_LIMIT_SEARCH_HISTORY).map((item: SearchHistory) => (
                            <button
                                className="btn btn-link dropdown-item search-list-item"
                                key={item.id}
                                onClick={() => onTermPressHandler(item.term)}
                            >
                                <i className="material-icons-outlined">{Constants.icons.SEARCH}</i>
                                <span className="recent-search-text">{item.term}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="search-dropdown dropdown">
                <button className="btn btn-danger dropdown-toggle" data-toggle="dropdown" ref={dropdownSuggestBtnRef} type="button"></button>
                <div className="search-dropdown-container dropdown-menu dropdown-menu-right" ref={dropdownSuggestContainerRef}>
                    <div className="search-list">
                        {suggestionsList.map((item: string, index: number) => (
                            <button
                                className="btn btn-link dropdown-item search-list-item"
                                key={`${item}_${index}`}
                                onClick={() => onTermPressHandler(item)}
                            >
                                <span className="recent-search-text">{item}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Search;
